/**
 * Stores Dashboard HTML renderer for mcp-ui.
 *
 * Renders a responsive card grid of knowledge stores.
 * Self-contained — all styles inline, no external dependencies.
 */

import { BASE_STYLES } from './styles.js';

export interface StoreData {
  id: string;
  name: string;
  type: 'repo' | 'file' | 'web';
  path?: string | undefined;
  url?: string | undefined;
  branch?: string | undefined;
  depth?: number | undefined;
  maxPages?: number | undefined;
  tags?: readonly string[] | undefined;
  description?: string | undefined;
  modelId?: string | undefined;
  createdAt: string;
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function formatDate(isoDate: string): string {
  const date = new Date(isoDate);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${String(diffDays)}d ago`;
  if (diffDays < 30) return `${String(Math.floor(diffDays / 7))}w ago`;
  if (diffDays < 365) return `${String(Math.floor(diffDays / 30))}mo ago`;
  return `${String(Math.floor(diffDays / 365))}y ago`;
}

function getTypeIcon(type: 'repo' | 'file' | 'web'): string {
  switch (type) {
    case 'repo':
      return `<svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor"><path d="M2 2.5A2.5 2.5 0 014.5 0h8.75a.75.75 0 01.75.75v12.5a.75.75 0 01-.75.75h-2.5a.75.75 0 110-1.5h1.75v-2h-8a1 1 0 00-.714 1.7.75.75 0 01-1.072 1.05A2.495 2.495 0 012 11.5v-9zm10.5-1h-8a1 1 0 00-1 1v6.708A2.486 2.486 0 014.5 9h8V1.5zM5 12.25v3.25a.25.25 0 00.4.2l1.45-1.087a.25.25 0 01.3 0L8.6 15.7a.25.25 0 00.4-.2v-3.25a.25.25 0 00-.25-.25h-3.5a.25.25 0 00-.25.25z"/></svg>`;
    case 'file':
      return `<svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor"><path d="M3.75 1.5a.25.25 0 00-.25.25v11.5c0 .138.112.25.25.25h8.5a.25.25 0 00.25-.25V6H9.75A1.75 1.75 0 018 4.25V1.5H3.75zm5.75.56v2.19c0 .138.112.25.25.25h2.19L9.5 2.06zM2 1.75C2 .784 2.784 0 3.75 0h5.086c.464 0 .909.184 1.237.513l3.414 3.414c.329.328.513.773.513 1.237v8.086A1.75 1.75 0 0112.25 15h-8.5A1.75 1.75 0 012 13.25V1.75z"/></svg>`;
    case 'web':
      return `<svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor"><path d="M1.5 8a6.5 6.5 0 1113 0 6.5 6.5 0 01-13 0zM8 0a8 8 0 100 16A8 8 0 008 0zM6.379 5.227A31.17 31.17 0 006 7.5h4a31.17 31.17 0 00-.379-2.273c-.27.096-.56.17-.862.22a4.463 4.463 0 00-.518.06 4.463 4.463 0 00-.518-.06c-.303-.05-.592-.124-.862-.22zM5.43 7.5a32.81 32.81 0 01.42-2.667 8.202 8.202 0 01-.987-.652A6.537 6.537 0 003.538 7.5H5.43zm-1.893 1.5h1.893c.05.898.155 1.787.42 2.667a8.202 8.202 0 01-.987.652A6.537 6.537 0 013.538 9zm6.963 0a32.81 32.81 0 01-.42 2.667c.326.217.668.41.987.652A6.537 6.537 0 0012.462 9h-1.893zm1.893-1.5h-1.893a32.81 32.81 0 00-.42-2.667c.326-.217.668-.41.987-.652A6.537 6.537 0 0112.462 7.5zM6 9h4a31.17 31.17 0 01-.379 2.273 4.708 4.708 0 01-.862-.22 4.463 4.463 0 01-.518-.06 4.463 4.463 0 01-.518.06c-.303.05-.592.124-.862.22A31.17 31.17 0 016 9zm2-6.97a3.26 3.26 0 01.762.292c.243.12.474.263.69.427A31.682 31.682 0 009.131 1.5 6.524 6.524 0 008 2.03zm0 11.94c.372-.06.753-.244 1.131-.53a31.682 31.682 0 00-.32-1.248 3.26 3.26 0 01-.762.292 4.463 4.463 0 01-.05.007v1.48zm0-11.94V2.03a3.26 3.26 0 00-.762.292c-.243.12-.474.263-.69.427.107.397.21.812.32 1.248A6.524 6.524 0 008 2.03zm0 11.94v-1.48a4.463 4.463 0 00.05.007 3.26 3.26 0 00.762.292c.107-.397.21-.812.32-1.248-.216.164-.447.307-.69.427A3.26 3.26 0 008 13.97z"/></svg>`;
  }
}

function renderStoreCard(store: StoreData): string {
  const statusClass = store.modelId !== undefined ? 'ready' : 'unknown';
  const statusLabel = store.modelId !== undefined ? 'Ready' : 'Not indexed';

  const location = store.path ?? store.url ?? '';
  const truncatedLocation =
    location.length > 60 ? `...${location.slice(location.length - 57)}` : location;

  const tagsHtml =
    store.tags !== undefined && store.tags.length > 0
      ? `<div class="card-tags">
          ${store.tags.map((t) => `<span class="tag">${escapeHtml(t)}</span>`).join('')}
        </div>`
      : '';

  const descriptionHtml =
    store.description !== undefined
      ? `<p class="card-description">${escapeHtml(store.description)}</p>`
      : '';

  const branchHtml =
    store.branch !== undefined
      ? `<span class="meta-separator"></span>
         <span class="mono">${escapeHtml(store.branch)}</span>`
      : '';

  const modelHtml =
    store.modelId !== undefined
      ? `<span class="meta-separator"></span>
         <span class="mono" title="Embedding model">${escapeHtml(store.modelId)}</span>`
      : '';

  return `
    <div class="store-card">
      <div class="card-header">
        <div class="card-title-row">
          <div class="card-status">
            <span class="status-dot status-${statusClass}" title="${statusLabel}"></span>
          </div>
          <h3 class="card-title">${escapeHtml(store.name)}</h3>
          <span class="badge badge-${store.type}">
            ${getTypeIcon(store.type)}
            <span style="margin-left: 4px">${store.type}</span>
          </span>
        </div>
        ${descriptionHtml}
      </div>
      <div class="card-body">
        ${
          location.length > 0
            ? `<div class="card-location mono truncate" title="${escapeHtml(location)}">${escapeHtml(truncatedLocation)}</div>`
            : ''
        }
        ${tagsHtml}
      </div>
      <div class="card-footer">
        <div class="meta-row">
          <span title="${escapeHtml(store.createdAt)}">${formatDate(store.createdAt)}</span>
          ${branchHtml}
          ${modelHtml}
        </div>
      </div>
    </div>
  `;
}

export function renderStoresDashboard(stores: StoreData[]): string {
  const storesByType = {
    repo: stores.filter((s) => s.type === 'repo').length,
    file: stores.filter((s) => s.type === 'file').length,
    web: stores.filter((s) => s.type === 'web').length,
  };

  const indexed = stores.filter((s) => s.modelId !== undefined).length;

  const summaryParts: string[] = [];
  if (storesByType.repo > 0) summaryParts.push(`${String(storesByType.repo)} repo`);
  if (storesByType.file > 0) summaryParts.push(`${String(storesByType.file)} file`);
  if (storesByType.web > 0) summaryParts.push(`${String(storesByType.web)} web`);

  const contentHtml =
    stores.length === 0
      ? `<div class="empty-state">
           <div class="empty-state-title">No knowledge stores</div>
           <p>Create a store to start indexing code and documentation.</p>
         </div>`
      : `<div class="store-grid">
           ${stores.map((store) => renderStoreCard(store)).join('')}
         </div>`;

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Knowledge Stores</title>
  <style>
    ${BASE_STYLES}

    /* Dashboard-specific styles */
    .dashboard-stats {
      display: flex;
      gap: var(--space-lg);
      margin-bottom: var(--space-xl);
      flex-wrap: wrap;
    }

    .stat-card {
      background: var(--bg-secondary);
      border: 1px solid var(--border-primary);
      border-radius: var(--radius-lg);
      padding: var(--space-md) var(--space-lg);
      display: flex;
      flex-direction: column;
      min-width: 100px;
    }

    .stat-value {
      font-size: var(--font-size-2xl);
      font-weight: 700;
      color: var(--text-primary);
      line-height: 1.2;
    }

    .stat-label {
      font-size: var(--font-size-xs);
      color: var(--text-tertiary);
      text-transform: uppercase;
      letter-spacing: 0.05em;
      margin-top: 2px;
    }

    /* Store card grid */
    .store-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
      gap: var(--space-lg);
    }

    @media (max-width: 768px) {
      .store-grid {
        grid-template-columns: 1fr;
      }
    }

    .store-card {
      background: var(--bg-card);
      border: 1px solid var(--border-primary);
      border-radius: var(--radius-lg);
      overflow: hidden;
      transition: border-color var(--transition-normal), box-shadow var(--transition-normal);
      display: flex;
      flex-direction: column;
    }

    .store-card:hover {
      border-color: var(--border-primary);
      box-shadow: var(--shadow-md);
      background: var(--bg-card-hover);
    }

    .card-header {
      padding: var(--space-lg) var(--space-lg) var(--space-sm);
    }

    .card-title-row {
      display: flex;
      align-items: center;
      gap: var(--space-sm);
    }

    .card-status {
      display: flex;
      align-items: center;
      flex-shrink: 0;
    }

    .card-title {
      font-size: var(--font-size-md);
      font-weight: 600;
      color: var(--text-primary);
      flex: 1;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .card-description {
      font-size: var(--font-size-sm);
      color: var(--text-secondary);
      margin-top: var(--space-sm);
      line-height: 1.5;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }

    .card-body {
      padding: var(--space-sm) var(--space-lg);
      flex: 1;
    }

    .card-location {
      color: var(--text-tertiary);
      font-size: var(--font-size-sm);
    }

    .card-tags {
      display: flex;
      flex-wrap: wrap;
      gap: var(--space-xs);
      margin-top: var(--space-sm);
    }

    .card-footer {
      padding: var(--space-sm) var(--space-lg) var(--space-lg);
      border-top: 1px solid var(--border-secondary);
      margin-top: auto;
    }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1 class="header-title">Knowledge Stores</h1>
      <p class="header-subtitle">${String(stores.length)} store${stores.length !== 1 ? 's' : ''}${summaryParts.length > 0 ? ` — ${summaryParts.join(', ')}` : ''}</p>
    </div>
    <div class="dashboard-stats">
      <div class="stat-card">
        <span class="stat-value">${String(stores.length)}</span>
        <span class="stat-label">Total Stores</span>
      </div>
      <div class="stat-card">
        <span class="stat-value">${String(indexed)}</span>
        <span class="stat-label">Indexed</span>
      </div>
      <div class="stat-card">
        <span class="stat-value">${String(stores.length - indexed)}</span>
        <span class="stat-label">Pending</span>
      </div>
    </div>
    ${contentHtml}
  </div>
</body>
</html>`;
}
